import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info);

    // Check if it's a dynamic import failure (common after new deployments)
    const isChunkLoadError =
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.name === 'ChunkLoadError' ||
      error.message.includes('loading chunk');

    if (isChunkLoadError) {
      console.warn('Dynamic import failure detected. Auto-reloading page for latest updates...');
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex min-h-[400px] w-full flex-col items-center justify-center p-8 text-center bg-muted rounded-2xl border border-border shadow-sm relative overflow-hidden">
          {/* Decorative Background */}
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-red-500/0 via-red-500/50 to-red-500/0" />

          <div className="relative z-10 flex flex-col items-center max-w-md w-full">
            <div className="h-16 w-16 bg-card border border-border shadow-glass rounded-2xl flex items-center justify-center mb-6">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>

            <div className="space-y-3 mb-8">
              <h2 className="text-2xl md:text-3xl font-black text-foreground tracking-tight">
                System Exception
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed font-medium">
                {this.state.error?.message ||
                  'An unexpected operational error occurred within this component.'}
              </p>
            </div>

            <button
              onClick={() => this.setState({ hasError: false })}
              className="flex items-center border-none justify-center gap-2 rounded-xl bg-foreground text-background px-6 py-3 h-11 text-[11px] font-black uppercase tracking-widest hover:bg-foreground/90 transition-all active:scale-95 shadow-lg shadow-black/5"
            >
              <RefreshCw className="h-4 w-4" />
              Reset & Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
